import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/Button'
import {
  TREE_QUESTIONS, CAREER_TREES, recommendTree, tierLabel
} from '../content/onboardingData'
import './TreeSelection.css'

const PHASE = { QUIZ: 'quiz', PICK: 'pick' }

export function TreeSelection() {
  const { user, profile, refreshProfile } = useAuth()
  const navigate = useNavigate()

  const [phase, setPhase] = useState(PHASE.QUIZ)
  const [answers, setAnswers] = useState({})
  const [currentQ, setCurrentQ] = useState(0)
  const [recommended, setRecommended] = useState(null)
  const [selected, setSelected] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  function handleAnswer(questionId, optionId) {
    const updated = { ...answers, [questionId]: optionId }
    setAnswers(updated)

    if (currentQ < TREE_QUESTIONS.length - 1) {
      setCurrentQ(prev => prev + 1)
    } else {
      setRecommended(recommendTree(updated))
      setPhase(PHASE.PICK)
    }
  }

  async function handleConfirm() {
    if (!selected) return
    setSaving(true)
    setSaveError('')

    try {
      const { error } = await supabase.from('profiles')
        .update({
          selected_tree:    selected,
          tree_selected_at: new Date().toISOString(),
          updated_at:       new Date().toISOString(),
        })
        .eq('id', user.id)

      if (error) {
        // Surface the failure rather than navigating to a half-set state.
        // Without this, the user would land on /dashboard with no
        // selected_tree and see the default tree silently.
        console.error('TreeSelection: profile update failed:', error)
        setSaveError('Could not save your selection. Please try again.')
        setSaving(false)
        return
      }

      await refreshProfile()
      navigate('/dashboard')

    } catch (e) {
      // Network-level exception (e.g. offline). Same recovery path.
      console.error('TreeSelection: profile update threw:', e)
      setSaveError('Could not save your selection. Check your connection and try again.')
      setSaving(false)
    }
  }

  const question = TREE_QUESTIONS[currentQ]
  const progressPct = (currentQ / TREE_QUESTIONS.length) * 100
  const placedTier = profile?.placement_tier || 1

  // ── Tree picker ──
  if (phase === PHASE.PICK) {
    const recommendedTree = CAREER_TREES.find(t => t.id === recommended)

    return (
      <div className="tree-shell">
        <div className="tree-card-outer">
          <div className="tree-eyebrow mono">Foundation complete — choose your path</div>
          <h1 className="tree-title">Which tree are you building?</h1>

          {/* Recommendation */}
          {recommendedTree && (
            <div className="tree-recommendation">
              <span className="tree-recommendation__label mono">Recommended for you</span>
              <div className="tree-recommendation__tree">
                <span className="tree-recommendation__icon">{recommendedTree.icon}</span>
                <div>
                  <div className="tree-recommendation__name">{recommendedTree.label}</div>
                  <div className="tree-recommendation__desc">{recommendedTree.description}</div>
                </div>
              </div>
              <p className="tree-recommendation__note">
                Based on your answers. You can choose any path — this is a suggestion, not a lock-in.
              </p>
            </div>
          )}

          {/* Tier 2 push */}
          {placedTier > 1 && (
            <div className="tree-tier-note">
              <span className="tree-tier-note__badge mono">Tier {placedTier} placement</span>
              <span>
                Your experience assessment recommended <strong>Tier {placedTier} — {tierLabel(placedTier)}</strong>.
                Once you select your tree, you'll see your upgrade options.
              </span>
            </div>
          )}

          {/* Tree grid */}
          <div className="tree-grid">
            {CAREER_TREES.map(tree => (
              <button
                key={tree.id}
                className={`tree-option ${selected === tree.id ? 'tree-option--selected' : ''} ${recommended === tree.id ? 'tree-option--recommended' : ''}`}
                onClick={() => { setSelected(tree.id); setSaveError('') }}
              >
                {recommended === tree.id && (
                  <span className="tree-option__rec-tag mono">Recommended</span>
                )}
                <div className="tree-option__icon">{tree.icon}</div>
                <div className="tree-option__label">{tree.label}</div>
                <div className="tree-option__desc">{tree.description}</div>
                <div className="tree-option__suits">{tree.suits}</div>
                {selected === tree.id && (
                  <div className="tree-option__check" aria-hidden="true">✓</div>
                )}
              </button>
            ))}
          </div>

          {saveError && (
            <div style={{
              color: '#ff6b6b',
              fontFamily: 'var(--font-mono)',
              fontSize: '0.78rem',
              textAlign: 'center',
              marginBottom: '12px',
            }}>
              {saveError}
            </div>
          )}

          <Button
            fullWidth
            size="lg"
            disabled={!selected}
            loading={saving}
            onClick={handleConfirm}
          >
            Confirm path — {selected ? CAREER_TREES.find(t => t.id === selected)?.label : 'select one above'}
          </Button>

          <p className="tree-unlock-note">
            Additional trees can be unlocked later. You're committing to a starting direction, not a permanent cage.
          </p>
        </div>
      </div>
    )
  }

  // ── Quiz ──
  return (
    <div className="tree-shell">
      <div className="tree-card-outer tree-card-outer--quiz">
        <div className="tree-eyebrow mono">
          Foundation complete · Career direction — {currentQ + 1} of {TREE_QUESTIONS.length}
        </div>
        <h1 className="tree-title">A few questions before you choose.</h1>
        <p className="tree-sub">
          8 questions to point you in the right direction. You make the final call.
        </p>

        <div className="onboarding-progress">
          <div className="onboarding-progress__bar">
            <div className="onboarding-progress__fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="onboarding-progress__label mono">{currentQ + 1} / {TREE_QUESTIONS.length}</span>
        </div>

        <div className="onboarding-question">
          <p className="onboarding-question__text">{question.text}</p>
          <div className="onboarding-options">
            {question.options.map(opt => (
              <button
                key={opt.id}
                className="onboarding-option"
                onClick={() => handleAnswer(question.id, opt.id)}
              >
                <span className="onboarding-option__letter mono">{opt.id.toUpperCase()}</span>
                <span className="onboarding-option__text">{opt.text}</span>
              </button>
            ))}
          </div>
        </div>

        <button
          className="onboarding-skip"
          onClick={() => { setRecommended(null); setPhase(PHASE.PICK) }}
        >
          Skip quiz — just show me the trees
        </button>
      </div>
    </div>
  )
}
